import React, { useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

export const preparationStatusLabels = {
  queued: 'En attente d’analyse', processing: 'Lecture du document en cours',
  completed: 'Métadonnées vérifiées', review: 'Métadonnées à vérifier',
  failed: 'Échec de lecture : saisie ou relance nécessaire', blocked: 'Mapping à corriger',
  skipped: 'Extraction non demandée',
};

const origins = { csv: 'CSV', default: 'Valeur par défaut', manual: 'Saisie manuelle', extraction: 'Document' };
const format = (value) => value === null || value === undefined ? '' : String(value);
const hasValue = (value) => value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '');

export default function ImportPreparationFields({ item, index, disabled, onChange }) {
  const [optionalOpen, setOptionalOpen] = useState(false);
  const fields = item.fields || [];
  const optionalFields = fields.filter((field) => !field.required);
  const optionalTitleId = `preparation-${index}-optional-title`;
  const closeOptional = () => setOptionalOpen(false);
  return <div className="import-preparation-fields">
    {!fields.length && <span className="text-muted">Aucun champ défini pour ce profil.</span>}
    {fields.filter((field) => field.required || hasValue(item.metadata?.[field.key])
      || hasValue(item.conflicts?.[field.key]?.suggested)
      || (item.ambiguous?.[field.key] || []).some((candidate) => hasValue(candidate.value)))
      .map((field) => <PreparationField key={field.key} field={field} item={item} index={index} disabled={disabled} onChange={onChange} />)}
    {optionalFields.length > 0 && <button type="button" className="import-optional-metadata-badge"
      aria-haspopup="dialog" aria-expanded={optionalOpen} disabled={disabled}
      onClick={() => setOptionalOpen(true)}>
      Métadonnées facultatives <span>({optionalFields.length})</span>
    </button>}
    {optionalOpen && <Modal isOpen toggle={closeOptional} labelledBy={optionalTitleId}
      className="import-optional-metadata-modal" centered scrollable trapFocus>
      <ModalHeader id={optionalTitleId} toggle={closeOptional}>Métadonnées facultatives</ModalHeader>
      <ModalBody>
        <p className="text-muted">{item.basename || item.file}</p>
        {optionalFields.map((field) => <PreparationField key={field.key} field={field} item={item}
          index={`optional-${index}`} disabled={disabled} onChange={onChange} />)}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={closeOptional}>Fermer</button>
      </ModalFooter>
    </Modal>}
    {(item.extraction?.errors || []).map((error, errorIndex) => <p className="text-danger" key={errorIndex}>{error}</p>)}
  </div>;
}

function PreparationField({ field, item, index, disabled, onChange }) {
  const [editing, setEditing] = useState(false);
  const editButton = useRef(null);
  const key = field.key;
  const label = field.label || key;
  const value = item.metadata?.[key];
  const origin = origins[item.provenance?.[key]];
  const conflict = item.conflicts?.[key];
  const evidence = item.extraction?.matches?.find((match) => match.key === key);
  const inputId = `preparation-${index}-${key}`;
  const warningId = `${inputId}-warning`;
  const editorProps = {
    id: inputId, className: 'form-control form-control-sm', autoFocus: true,
    'aria-label': `${label}${field.required ? ' *' : ''}`,
    'aria-invalid': Boolean(item.metadataWarnings?.[key]),
    'aria-describedby': item.metadataWarnings?.[key] ? warningId : undefined,
    onBlur: () => setEditing(false),
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        setEditing(false);
        editButton.current?.focus();
      }
    },
  };
  return <div className="metadata-batch-field" key={key}>
    <div className="import-metadata-heading">
      <span className="import-metadata-label">{label}{field.required ? ' *' : ''}</span>
      <button ref={editButton} type="button" className="import-metadata-edit" disabled={disabled}
        aria-label={`Modifier ${label}`} title={`Modifier ${label}`} aria-expanded={editing && !disabled}
        onClick={() => setEditing(true)}>
        <i className="ri-pencil-line" aria-hidden="true" />
      </button>
    </div>
    {editing && !disabled ? (field.type === 'boolean'
      ? <select {...editorProps}
        value={format(value)} onChange={(e) => onChange(key, e.target.value === '' ? null : e.target.value === 'true')}>
        <option value="">Choisir</option><option value="true">Oui</option><option value="false">Non</option>
      </select>
      : <input {...editorProps} value={format(value)} onChange={(e) => onChange(key, e.target.value)} />)
      : <div className={`import-metadata-value${!hasValue(value) ? ' is-empty' : ''}`}>
        {!hasValue(value) ? 'Non renseigné' : field.type === 'boolean' ? (value === true || value === 'true' ? 'Oui' : 'Non') : format(value)}
      </div>}
    {origin && <small className="import-metadata-origin">{origin}</small>}
    {item.metadataWarnings?.[key] && <small id={warningId} className="text-danger">{item.metadataWarnings[key]}</small>}
    {conflict && <div className="import-preparation-proposal">Valeur détectée différente : <strong>{format(conflict.suggested)}</strong>
      <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => onChange(key, conflict.suggested)}>Utiliser cette valeur</button>
    </div>}
    {(item.ambiguous?.[key] || []).map((candidate, candidateIndex) => <div className="import-preparation-proposal" key={candidateIndex}>
      Proposition : {format(candidate.value)} {candidate.page ? `(page ${candidate.page})` : ''}
      <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => onChange(key, candidate.value)}>Choisir</button>
    </div>)}
    {evidence && <small>Extrait {evidence.page ? `page ${evidence.page}` : ''} : {evidence.line || evidence.match}</small>}
  </div>;
}
